import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useUpdateCartItemRequestStatusMutation, useLazyGetCartItemRequestByIdQuery } from "../../../../redux/services/cartToCartOrder";
import BreadCrumbs from "../../../../components/BreadCrumbs";
import {
  Card,
  CardBody,
  Spinner,
  Alert,
  Modal,
  Button
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
} from "@fortawesome/free-solid-svg-icons";
import {
  capitalizeFirstLetter,
  formatTimestamp,
  InventoryId,
} from "../../../../utility/Utility";
import "./index.css";
import ButtonComponent from "../../../../components/ButtonComponent";
import { useFindOrderDataByOrderIdMutation } from "../../../../redux/services/orderService";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { useLazyGetCartInventoryForRestaurantQuery } from "../../../../redux/services/restaurantServices";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

export interface CartItemRequest {
  id: string;
  cart?: {
    id: string;
    displayName: string;
  };
  restaurantId: string;
  orderItems: Array<{
    displayName: string;
    menuItemId: string;
    quantity: number;
    unitPrice: number;
    itemType: string;
    portionSize: string | null;
  }>;
  createdAt: {
    _seconds: number;
    _nanoseconds: number;
  };
  orders?: Array<{
    toCartId?: string;
    orderId: string;
    fromCartId?: string;
  }>;
}

const CartItemRequestView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [
    triggerGetCartItemRequest,
    { data: cartItemRequestData, isLoading, error },
  ] = useLazyGetCartItemRequestByIdQuery();

  const [showOrderModal, setShowOrderModal] = useState(false);
  const [modalOrderId, setModalOrderId] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isStatusUpdateLoading, setIsStatusUpdateLoading] = useState(false);
  const [statusAction, setStatusAction] = useState<null | "cancel" | "complete">(null);
  const [findOrderDataByOrderId, { isLoading: isModalOrderLoading, data: modalOrderData }] =
    useFindOrderDataByOrderIdMutation();
  const MySwal = withReactContent(Swal);

  const selectedRestaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails?.id
  );
  const [getCartInventory, { data: cartInventoryData }] =
    useLazyGetCartInventoryForRestaurantQuery();
  useEffect(() => {
    if (id) {
      triggerGetCartItemRequest(id);
    }
  }, [id, triggerGetCartItemRequest]);



  const cartNameFromState = location.state?.cartName;
  const cartItemRequestFromState = location.state?.cartItemRequest;

  const currentCartItemRequest = cartItemRequestData?.itemRequest?.request || cartItemRequestFromState;
  const cartName = cartNameFromState || currentCartItemRequest?.cart?.displayName || 'Cart Request Details';

  // Create custom name mapping for breadcrumb
  const breadcrumbNameMap = {
    [id!]: cartName
  };

  const openOrderModal = (orderId: string) => {
    setModalOrderId(orderId);
    setShowOrderModal(true);
    // trigger fetch (mutation returns a promise but data is available in modalOrderData)
    findOrderDataByOrderId(orderId);
  };

  const closeOrderModal = () => {
    setShowOrderModal(false);
    setModalOrderId(null);
  };



  // Safely extract request and orderDetails only if data is available
  const request = cartItemRequestData?.itemRequest?.request;
  const orderDetails = cartItemRequestData?.itemRequest?.orderDetails;
  const inventoryId: string = InventoryId(new Date());
  useEffect(() => {
    // Fetch inventory for this cart when request is loaded
    if (request?.cart?.id && selectedRestaurantId) {
      getCartInventory({
        cartId: request.cart.id,
        restaurantId: selectedRestaurantId,
        inventoryId: inventoryId
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.cart?.id, selectedRestaurantId]);

  // Show only detailed orders whose orderId appears in request.orders with a fromCartId
  const filteredOrderDetails = (() => {
    if (!request || !orderDetails) return [];
    const orderIdsFromSource = new Set<string>();
    (request.orders || []).forEach((o: any) => {
      if (o?.fromCartId && o?.orderId) orderIdsFromSource.add(o.orderId);
    });
    // orderDetails contains the detailed order objects; filter them by the set
    return (orderDetails || []).filter((d: any) => orderIdsFromSource.has(d.id));
  })();

  const getCartName = () => {
    return request?.cart?.displayName || "No Cart Assigned";
  };

  const calculateTotalPrice = () => {
    if (!request) return 0;
    return request.orderItems.reduce((total, item) => {
      return total + item.unitPrice * item.quantity;
    }, 0);
  };

  const handlePlaceOrder = () => {
    if (!request) return;
    navigate("/orders/cart-item-request/create-cart-to-cart-order", {
      state: {
        cartRequest: request,
      },
    });
  };

  // Determine if the request was created today (local date)
  const isCreatedToday = (() => {
    if (!request?.createdAt) return false;
    try {
      const seconds = (request.createdAt._seconds || 0) * 1000;
      const created = new Date(seconds);
      const now = new Date();
      const createdYMD = `${created.getFullYear()}-${String(created.getMonth() + 1).padStart(2, "0")}-${String(created.getDate()).padStart(2, "0")}`;
      const nowYMD = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      return createdYMD === nowYMD;
    } catch (err) {
      return false;
    }
  })();

  const [updateCartItemRequestStatus] = useUpdateCartItemRequestStatusMutation();

  // Add cancel handler
  const handleCancelRequest = async () => {
    const result = await MySwal.fire({
      title: "Are you sure?",
      text: "Do you want to cancel this request?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, cancel request",
      cancelButtonText: "No",
      customClass: {
        confirmButton: "btn btn-danger swal-btn-gap",
        cancelButton: "btn btn-secondary swal-btn-gap",
      },
      buttonsStyling: false,
    });
    if (result.isConfirmed && request) {
      setIsStatusUpdateLoading(true);
      setStatusAction("cancel");
      const response: any = await updateCartItemRequestStatus({ requestId: request.id, status: "Cancelled" });
      if (response?.data === "CartItemRequest updated Successfuly") {
        MySwal.fire({
          title: "Request Cancelled",
          text: "The request has been cancelled.",
          icon: "success",
          confirmButtonText: "OK",
          customClass: { confirmButton: "btn btn-success" },
          buttonsStyling: false,
        });
        navigate("/orders/cart-item-request");
        setIsStatusUpdateLoading(false);
        setStatusAction(null);
      } else {
        toast.error("Could not cancel the request please try again.");
        setIsStatusUpdateLoading(false);
        setStatusAction(null);
      }
    }
  };

  const handleCompleteRequest = async () => {
    setIsStatusUpdateLoading(true);
    setStatusAction("complete");
    const result = await MySwal.fire({
      title: "Are you sure?",
      text: "Do you want to complete this request?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, complete request",
      cancelButtonText: "No",
      customClass: {
        confirmButton: "btn btn-success swal-btn-gap",
        cancelButton: "btn btn-secondary swal-btn-gap",
      },
      buttonsStyling: false,
    });
    if (result.isConfirmed && request) {
      const response: any = await updateCartItemRequestStatus({ requestId: request.id, status: "Completed" });
      if (response?.data === "CartItemRequest updated Successfuly") {
        MySwal.fire({
          title: "Request Completed",
          text: "The request has been completed.",
          icon: "success",
          confirmButtonText: "OK",
          customClass: { confirmButton: "btn btn-success" },
          buttonsStyling: false,
        });
        setIsStatusUpdateLoading(false);
        setStatusAction(null);
        navigate("/orders/cart-item-request");
      } else {
        toast.error("Could not complete the request please try again.");
        setIsStatusUpdateLoading(false);
        setStatusAction(null);
      }
    } else {
      // if not confirmed, clear the action
      setIsStatusUpdateLoading(false);
      setStatusAction(null);
    }
  };

  // Calculate fulfilled quantity (items already in existing orders)
  const getFulfilledCount = (item: any) => {
    if (!orderDetails || !request) return 0;
    // Sum quantity for this menuItemId in non-cancelled existing orders
    let fulfilledQty = 0;
    for (const od of orderDetails) {
      if (od.orderStatus === "Cancelled") continue;
      const found = (od.orderItems || []).find((oi: any) => oi.menuItemId === item.menuItemId);
      if (found) fulfilledQty += found.quantity || 0;
    }
    return fulfilledQty;
  };

  // Calculate pending count for each item (requested - fulfilled)
  const getPendingCount = (item: any) => {
    if (!orderDetails || !request) return item.quantity;
    const fulfilledQty = getFulfilledCount(item);
    return Math.max(0, item.quantity - fulfilledQty);
  };

  // Keep the old function for backward compatibility (but update it to use the new logic)
  // const getAvailableInOrdersCount = (item: any) => {
  //   return getPendingCount(item);
  // };


  // Calculate inventory for each item using inventory API response
  const getInventoryCount = (item: any) => {
    if (!isCreatedToday) return 0;
    if (!cartInventoryData?.items || !item.menuItemId) {
      return 0;
    }
    const portionSize = item.portionSize || "REGULAR";
    const itemInventory = cartInventoryData.items[item.menuItemId]?.[portionSize];
    if (!itemInventory) return 0;
    const {
      totalReceived = 0,
      totalReserved = 0,
      directIssues = 0,
      totalReturned = 0,
      cartTransferCount = 0,
    } = itemInventory;
    // Inventory = totalReceived - [totalReserved + directIssues + totalReturned + cartTransferCount]
    return Math.max(
      0,
      totalReceived -
      (totalReserved + directIssues + totalReturned + cartTransferCount)
    );
  };

  if (isLoading) {
    return (
      <div className="loading-center d-flex justify-content-center align-items-center">
        <Spinner animation="border" variant="primary" />
        <span className="ms-2">Loading cart item request...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger m-3">
        <h4>Error loading cart item request</h4>
        <p>There was an error loading the cart item request details.</p>
        <ButtonComponent onClick={() => navigate(-1)} titel="Go Back" />
      </div>
    );
  }

  if (!request) {
    return (
      <div className="alert alert-warning m-3">
        <h4>Cart Item Request Not Found</h4>
        <p>The requested cart item request could not be found.</p>
        <ButtonComponent onClick={() => navigate(-1)} titel="Go Back" />
      </div>
    );
  }

  return (
    <>
      <BreadCrumbs hasSubMenus={true} customNameMap={breadcrumbNameMap} />
      <div className="cart-item-request-view-container">
        {/* Request Details Card */}
        <Card className="cart-item-request-detail-card">
          <CardBody className="cart-item-detail-card-body">
            <div className="d-flex align-items-center gap-2">
              <span className="cart-item-detail-label">Cart Name:</span>
              <span className="cart-item-detail-value">{getCartName()}</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <span className="cart-item-detail-label">Created Time:</span>
              <span className="cart-item-detail-value">
                {formatTimestamp(request.createdAt)}
              </span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <ButtonComponent onClick={handlePlaceOrder} titel="+ New Transfer" isDisabled={!isCreatedToday} />
              {(request?.status === 'Pending') && (
                <>
                  <ButtonComponent onClick={handleCompleteRequest} titel={statusAction === "complete" ? "Completing..." : "Complete"} isDisabled={!!statusAction} />
                  <ButtonComponent onClick={handleCancelRequest} titel={statusAction === "cancel" ? "Cancelling..." : "Cancel"} isDisabled={!!statusAction} />
                </>
              )}
              {request?.status === 'Cancelled' &&
                (<span className="text-danger fw-bold">Request Cancelled</span>)}
              {request?.status === 'Completed' &&
                (<span className="text-success fw-bold">Request Completed</span>)}
            </div>
          </CardBody>
        </Card>

        {/* Requested Items Card */}
        <Card className="cart-item-request-card">
          <CardBody>
            <h6 className="cart-item-section-title">Requested Items</h6>
            {request.orderItems && request.orderItems.length > 0 ? (
              <table className="table cart-item-compact-table">
                <thead>
                  <tr>
                    <th className="cart-item-compact-table-header">#</th>
                    <th className="cart-item-compact-table-header">Item Name</th>
                    <th className="cart-item-compact-table-header">Requested Quantity</th>
                    <th className="cart-item-compact-table-header">Inventory</th>
                    <th className="cart-item-compact-table-header">Items in Orders Placed</th>
                    <th className="cart-item-compact-table-header">Pending</th>
                    <th className="cart-item-compact-table-header">Per Item Price (LKR)</th>
                    <th className="cart-item-compact-table-header">Item Type</th>
                    <th className="cart-item-compact-table-header">Portion Size</th>
                    <th className="cart-item-compact-table-header">Quantity</th>
                    <th className="cart-item-compact-table-header">Total (LKR)</th>
                  </tr>
                </thead>
                <tbody>
                  {request.orderItems.map((item, index) => (
                    <tr
                      key={item.menuItemId || index}
                      className="cart-item-details-row"
                    >
                      <td className="cart-item-compact-table-cell">
                        {index + 1}.
                      </td>
                      <td className="cart-item-compact-table-cell">
                        {item.displayName}
                      </td>
                      <td className="cart-item-compact-table-cell">
                        {item.quantity}
                      </td>
                      <td className="cart-item-compact-table-cell">
                        {getInventoryCount(item)}
                      </td>
                      <td className="cart-item-compact-table-cell">
                        {getFulfilledCount(item)}
                      </td>
                      <td className="cart-item-compact-table-cell">
                        {getPendingCount(item)}
                      </td>
                      <td className="cart-item-compact-table-cell">
                        {(item.unitPrice || 0).toFixed(2)}
                      </td>
                      <td className="cart-item-compact-table-cell">
                        {item.itemType?.replace?.(/([a-z])([A-Z])/g, "$1 $2") || ""}
                      </td>
                      <td className="cart-item-compact-table-cell">
                        {item.portionSize
                          ? capitalizeFirstLetter(item.portionSize)
                          : "N/A"}
                      </td>
                      <td className="cart-item-compact-table-cell">
                        {item.quantity}
                      </td>
                      <td className="cart-item-compact-table-cell">
                        {(item.unitPrice * item.quantity).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="cart-item-table-footer">
                    <td
                      className="cart-item-compact-table-cell"
                      colSpan={9}
                      style={{ textAlign: "right" }}
                    >
                      Grand Total:
                    </td>
                    <td className="cart-item-compact-table-cell cart-item-amount-highlight">
                      LKR {calculateTotalPrice().toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            ) : (
              <Alert variant="info" className="mt-3">
                No order items found for this request.
              </Alert>
            )}
          </CardBody>
        </Card>
        <Card className="cart-item-request-card mt-3">
          <CardBody>
            <h6 className="cart-item-section-title">Existing Orders for this Request</h6>
            {filteredOrderDetails && filteredOrderDetails.length > 0 ? (
              <div className="existing-orders-list">
                {filteredOrderDetails.map((orderWithDetails: any) => {
                  const od = orderWithDetails;
                  return (
                    <div key={od.id} className="mb-3 p-3 border rounded bg-white existing-order-row d-flex align-items-center justify-content-between">
                      <div>
                        <div style={{ fontWeight: 700 }}>Order #{od.orderReference}</div>
                        <div className="text-muted" style={{ fontSize: 13 }}>
                          Source: {od.cart?.displayName || "—"}
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-3">
                        <div className="text-muted" style={{ fontSize: 13 }}>
                          {od.orderItems?.length || 0} item{(od.orderItems?.length || 0) !== 1 ? "s" : ""}
                        </div>
                        <button
                          className="btn btn-outline-primary btn-sm"
                          title="View Order"
                          onClick={() => openOrderModal(od.id)}
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-muted">No existing orders for this request.</div>
            )}
          </CardBody>
        </Card>
        {/* Order Details Modal */}
        <Modal show={showOrderModal} onHide={closeOrderModal} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>Order Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {isModalOrderLoading ? (
              <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 140 }}>
                <Spinner animation="border" /> <span className="ms-2">Loading order...</span>
              </div>
            ) : modalOrderData ? (
              <>
                <div className="mb-2"><strong>Reference:</strong> {modalOrderData.orderReference}</div>
                <div className="mb-2"><strong>Created At:</strong> {formatTimestamp(modalOrderData.createdAt)}</div>
                <div className="mb-3"><strong>Total Price:</strong> LKR {(modalOrderData.totalPrice ?? modalOrderData.paymentSummary?.totalPrice ?? 0).toFixed(2)}</div>
                <h6>Items</h6>
                <table className="table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Item</th>
                      <th>Qty</th>
                      <th>Per Item (LKR)</th>
                      <th>Total (LKR)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(modalOrderData.orderItems || []).map((it: any, i: number) => (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td>{it.displayName}</td>
                        <td>{it.quantity}</td>
                        <td>{(it.unitPrice ?? 0).toFixed(2)}</td>
                        <td>{((it.unitPrice ?? 0) * (it.quantity ?? 0)).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : (
              <Alert variant="info">No order data available.</Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeOrderModal}>Close</Button>
            <Button variant="primary" onClick={() => { closeOrderModal(); navigate(`/orders/pickup-orders/view/`, { state: { orderId: modalOrderId } }); }}>Open Full View</Button>
          </Modal.Footer>
        </Modal>

        {/* Optionally show inventory loading/error/info */}
        {/* {isInventoryLoading && (
          <div className="text-info mb-2">Loading cart inventory...</div>
        )}
        {inventoryError && (
          <div className="text-danger mb-2">Error loading inventory.</div>
        )}
        {cartInventoryData && (
          <div className="mb-2">
            <strong>Cart Inventory:</strong>
            <pre style={{ fontSize: 12, background: "#f8f9fa", padding: 8, borderRadius: 4 }}>
              {JSON.stringify(cartInventoryData, null, 2)}
            </pre>
          </div>
        )} */}
      </div>
    </>
  );
};

export default CartItemRequestView;
